import { useState } from "react";

const COLORS = {
  navy: "#1A2B4A",
  navyMid: "#243557",
  navyLight: "#f0f3f8",
  red: "#C53D3F",
  redLight: "#fdf1f1",
  redBorder: "rgba(197,61,63,0.2)",
  border: "#e2e6ee",
  muted: "#6B7280",
  mutedLight: "#9CA3AF",
  white: "#FFFFFF",
  bg: "#F5F7FB",
  error: "#DC2626",
  success: "#059669",
  text: "#111827",
};

const styles = {
  page: {
    fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
    background: COLORS.bg,
    color: COLORS.text,
    lineHeight: "1.6",
    minHeight: "100vh",
  },
  hero: {
    background: `linear-gradient(135deg, ${COLORS.navy} 0%, ${COLORS.navyMid} 100%)`,
    padding: "72px 24px 64px",
    textAlign: "center",
    position: "relative",
    overflow: "hidden",
  },
  heroBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    padding: "6px 20px",
    background: "rgba(197,61,63,0.15)",
    border: "1px solid rgba(197,61,63,0.4)",
    borderRadius: "100px",
    color: "#f4a0a1",
    fontSize: "11px",
    fontWeight: "700",
    letterSpacing: "1.8px",
    textTransform: "uppercase",
    marginBottom: "18px",
  },
  heroDot: {
    width: "6px",
    height: "6px",
    background: COLORS.red,
    borderRadius: "50%",
    display: "inline-block",
  },
  heroTitle: {
    fontFamily: "'Playfair Display', Georgia, serif",
    fontSize: "clamp(1.8rem, 4vw, 2.8rem)",
    fontWeight: "700",
    color: COLORS.white,
    marginBottom: "14px",
    lineHeight: "1.2",
    letterSpacing: "-0.01em",
  },
  heroDesc: {
    fontSize: "15px",
    color: "rgba(255,255,255,0.7)",
    maxWidth: "520px",
    margin: "0 auto",
  },
  section: { padding: "52px 24px 72px" },
  container: { maxWidth: "820px", margin: "0 auto" },

  // Stepper
  stepper: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    position: "relative",
    marginBottom: "40px",
    paddingTop: "4px",
  },
  stepperLine: {
    position: "absolute",
    top: "19px",
    left: "calc(100% / 12)",
    right: "calc(100% / 12)",
    height: "2px",
    background: COLORS.border,
    zIndex: 0,
  },
  stepItem: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "10px",
    flex: 1,
    position: "relative",
    zIndex: 1,
  },
  stepBubble: (active, done) => ({
    width: "38px",
    height: "38px",
    borderRadius: "50%",
    border: `2px solid ${active ? COLORS.red : done ? COLORS.red : COLORS.border}`,
    background: active ? COLORS.red : done ? COLORS.redLight : COLORS.white,
    color: active ? COLORS.white : done ? COLORS.red : COLORS.mutedLight,
    fontSize: "13px",
    fontWeight: "700",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "all 0.25s",
    boxShadow: active ? `0 0 0 4px ${COLORS.redLight}` : "none",
    transform: active ? "scale(1.1)" : "scale(1)",
  }),
  stepLabel: (active, done) => ({
    fontSize: "10px",
    fontWeight: "700",
    letterSpacing: "0.5px",
    textTransform: "uppercase",
    color: active || done ? COLORS.red : COLORS.mutedLight,
    textAlign: "center",
    lineHeight: "1.3",
  }),

  // Card
  card: {
    background: COLORS.white,
    borderRadius: "14px",
    border: `1px solid ${COLORS.border}`,
    padding: "44px 48px",
    boxShadow: "0 2px 20px rgba(26,43,74,0.05)",
    marginBottom: "20px",
  },

  // Step header
  stepHead: {
    marginBottom: "36px",
    paddingBottom: "24px",
    borderBottom: `1px solid ${COLORS.border}`,
  },
  stepTag: {
    fontSize: "11px",
    fontWeight: "700",
    letterSpacing: "1.8px",
    textTransform: "uppercase",
    color: COLORS.red,
    marginBottom: "6px",
    display: "block",
  },
  stepTitle: {
    fontFamily: "'Playfair Display', Georgia, serif",
    fontSize: "clamp(1.4rem, 2.5vw, 1.85rem)",
    fontWeight: "700",
    color: COLORS.navy,
    marginBottom: "6px",
    lineHeight: "1.25",
  },
  stepDesc: { fontSize: "14px", color: COLORS.muted },

  // Grid
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: "20px",
  },
  fullCol: { gridColumn: "1 / -1" },

  // Fields
  field: { display: "flex", flexDirection: "column", gap: "6px" },
  label: {
    fontSize: "13px",
    fontWeight: "700",
    color: COLORS.navy,
    letterSpacing: "0.1px",
  },
  req: { color: COLORS.red, marginLeft: "2px" },
  input: (hasError) => ({
    width: "100%",
    padding: "11px 14px",
    border: `1.5px solid ${hasError ? COLORS.error : COLORS.border}`,
    borderRadius: "8px",
    fontSize: "14px",
    fontFamily: "inherit",
    color: COLORS.navy,
    background: COLORS.white,
    transition: "border-color 0.18s, box-shadow 0.18s",
    outline: "none",
    appearance: "none",
    WebkitAppearance: "none",
  }),
  textarea: (hasError) => ({
    width: "100%",
    padding: "11px 14px",
    border: `1.5px solid ${hasError ? COLORS.error : COLORS.border}`,
    borderRadius: "8px",
    fontSize: "14px",
    fontFamily: "inherit",
    color: COLORS.navy,
    background: COLORS.white,
    resize: "vertical",
    minHeight: "84px",
    outline: "none",
  }),
  select: (hasError) => ({
    width: "100%",
    padding: "11px 36px 11px 14px",
    border: `1.5px solid ${hasError ? COLORS.error : COLORS.border}`,
    borderRadius: "8px",
    fontSize: "14px",
    fontFamily: "inherit",
    color: COLORS.navy,
    background: `${COLORS.white} url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath fill='%236B7280' d='M6 8L0 0h12z'/%3E%3C/svg%3E") no-repeat right 14px center`,
    outline: "none",
    appearance: "none",
    WebkitAppearance: "none",
    cursor: "pointer",
  }),
  errorMsg: { fontSize: "12px", color: COLORS.error, fontWeight: "600" },

  // Subsection
  subsection: {
    background: COLORS.bg,
    borderRadius: "10px",
    padding: "26px 28px",
    marginBottom: "20px",
    border: `1px solid ${COLORS.border}`,
  },
  subsectionHead: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    marginBottom: "22px",
    paddingBottom: "14px",
    borderBottom: `1px solid ${COLORS.border}`,
  },
  subsectionBar: (primary) => ({
    width: "4px",
    height: "20px",
    borderRadius: "2px",
    background: primary ? COLORS.red : COLORS.border,
    flexShrink: 0,
  }),
  subsectionTitle: { fontSize: "15px", fontWeight: "700", color: COLORS.navy },

  // Service checkboxes
  serviceCheck: (checked) => ({
    display: "flex",
    alignItems: "center",
    gap: "14px",
    padding: "14px 18px",
    background: checked ? COLORS.redLight : COLORS.white,
    border: `1.5px solid ${checked ? COLORS.red : COLORS.border}`,
    borderRadius: "10px",
    cursor: "pointer",
    transition: "all 0.18s",
    marginBottom: "10px",
    userSelect: "none",
  }),
  checkBox: (checked) => ({
    width: "20px",
    height: "20px",
    borderRadius: "5px",
    border: `1.5px solid ${checked ? COLORS.red : COLORS.border}`,
    background: checked ? COLORS.red : COLORS.white,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    transition: "all 0.18s",
    color: COLORS.white,
    fontSize: "11px",
    fontWeight: "700",
  }),
  checkText: { fontSize: "14px", fontWeight: "500", color: COLORS.navy, flex: 1 },

  // Declaration
  declaration: {
    background: COLORS.navy,
    borderRadius: "12px",
    padding: "32px 36px",
    marginBottom: "4px",
  },
  declarationTitle: {
    fontFamily: "'Playfair Display', Georgia, serif",
    fontSize: "18px",
    fontWeight: "700",
    color: COLORS.white,
    marginBottom: "20px",
  },
  agreeRow: (checked, hasError) => ({
    display: "flex",
    alignItems: "flex-start",
    gap: "14px",
    padding: "16px 18px",
    background: checked ? "rgba(197,61,63,0.18)" : "rgba(255,255,255,0.07)",
    border: `1px solid ${hasError ? "#F87171" : checked ? "rgba(197,61,63,0.45)" : "rgba(255,255,255,0.12)"}`,
    borderRadius: "8px",
    marginBottom: "10px",
    cursor: "pointer",
    transition: "all 0.18s",
  }),
  agreeBox: (checked) => ({
    width: "20px",
    height: "20px",
    borderRadius: "5px",
    border: `1.5px solid ${checked ? COLORS.red : "rgba(255,255,255,0.35)"}`,
    background: checked ? COLORS.red : "rgba(255,255,255,0.08)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    marginTop: "1px",
    color: COLORS.white,
    fontSize: "11px",
    fontWeight: "700",
    transition: "all 0.18s",
  }),
  agreeText: {
    fontSize: "13.5px",
    color: "rgba(255,255,255,0.82)",
    lineHeight: "1.6",
  },
  note: {
    marginTop: "20px",
    padding: "14px 18px",
    background: "rgba(255,255,255,0.06)",
    border: "1px solid rgba(255,255,255,0.1)",
    borderLeft: `3px solid ${COLORS.red}`,
    borderRadius: "0 6px 6px 0",
    fontSize: "13px",
    color: "rgba(255,255,255,0.65)",
    lineHeight: "1.65",
  },

  // Nav
  nav: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    marginTop: "36px",
    paddingTop: "28px",
    borderTop: `1px solid ${COLORS.border}`,
  },
  btnPrimary: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    padding: "13px 30px",
    borderRadius: "8px",
    fontFamily: "inherit",
    fontSize: "14px",
    fontWeight: "700",
    cursor: "pointer",
    border: `1.5px solid ${COLORS.red}`,
    background: COLORS.red,
    color: COLORS.white,
    marginLeft: "auto",
    letterSpacing: "0.2px",
    transition: "opacity 0.15s, transform 0.15s",
  },
  btnOutline: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    padding: "13px 28px",
    borderRadius: "8px",
    fontFamily: "inherit",
    fontSize: "14px",
    fontWeight: "700",
    cursor: "pointer",
    border: `1.5px solid ${COLORS.border}`,
    background: COLORS.white,
    color: COLORS.navy,
    letterSpacing: "0.2px",
    transition: "border-color 0.15s",
  },
  btnSubmit: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    padding: "13px 36px",
    borderRadius: "8px",
    fontFamily: "inherit",
    fontSize: "14px",
    fontWeight: "700",
    cursor: "pointer",
    border: `1.5px solid ${COLORS.navy}`,
    background: COLORS.navy,
    color: COLORS.white,
    marginLeft: "auto",
    letterSpacing: "0.2px",
    transition: "opacity 0.15s",
  },

  // Help bar
  helpBar: {
    background: COLORS.white,
    border: `1px solid ${COLORS.border}`,
    borderRadius: "12px",
    padding: "18px 28px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "16px",
    flexWrap: "wrap",
  },
  helpText: { fontSize: "13.5px", color: COLORS.muted },
  helpContacts: { display: "flex", gap: "24px", flexWrap: "wrap" },
  helpContact: { fontSize: "13.5px", fontWeight: "600", color: COLORS.navy },
};

// ── Reusable Field Components ────────────────────────
const Field = ({ label, required, error, children, full }) => (
  <div style={{ ...styles.field, ...(full ? styles.fullCol : {}) }}>
    <label style={styles.label}>
      {label}
      {required && <span style={styles.req}>*</span>}
    </label>
    {children}
    {error && <span style={styles.errorMsg}>{error}</span>}
  </div>
);

const Input = ({ name, value, onChange, error, placeholder, type = "text", readOnly }) => (
  <input
    type={type}
    name={name}
    value={value}
    onChange={onChange}
    placeholder={placeholder}
    readOnly={readOnly}
    style={styles.input(!!error)}
    onFocus={e => { e.target.style.borderColor = COLORS.red; e.target.style.boxShadow = "0 0 0 3px rgba(197,61,63,0.1)"; }}
    onBlur={e => { e.target.style.borderColor = error ? COLORS.error : COLORS.border; e.target.style.boxShadow = "none"; }}
  />
);

const Select = ({ name, value, onChange, error, children }) => (
  <select
    name={name}
    value={value}
    onChange={onChange}
    style={styles.select(!!error)}
    onFocus={e => { e.target.style.borderColor = COLORS.red; }}
    onBlur={e => { e.target.style.borderColor = error ? COLORS.error : COLORS.border; }}
  >
    {children}
  </select>
);

const Textarea = ({ name, value, onChange, placeholder, rows = 3, full }) => (
  <textarea
    name={name}
    value={value}
    onChange={onChange}
    placeholder={placeholder}
    rows={rows}
    style={{ ...styles.textarea(false), ...(full ? styles.fullCol : {}) }}
    onFocus={e => { e.target.style.borderColor = COLORS.red; e.target.style.boxShadow = "0 0 0 3px rgba(197,61,63,0.1)"; }}
    onBlur={e => { e.target.style.borderColor = COLORS.border; e.target.style.boxShadow = "none"; }}
  />
);

const ServiceCheck = ({ name, checked, onChange, label, icon }) => (
  <label style={styles.serviceCheck(checked)} htmlFor={name}>
    <input type="checkbox" id={name} name={name} checked={checked} onChange={onChange} style={{ display: "none" }} />
    <span style={{ fontSize: "20px" }}>{icon}</span>
    <span style={styles.checkText}>{label}</span>
    <span style={styles.checkBox(checked)}>{checked ? "✓" : ""}</span>
  </label>
);

const AgreeRow = ({ name, checked, onChange, error, children }) => (
  <label style={styles.agreeRow(checked, !!error)} htmlFor={name}>
    <input type="checkbox" id={name} name={name} checked={checked} onChange={onChange} style={{ display: "none" }} />
    <span style={styles.agreeBox(checked)}>{checked ? "✓" : ""}</span>
    <span style={styles.agreeText}>{children}</span>
  </label>
);

// ── Steps ────────────────────────────────────────────
const steps = [
  { label: "Student" },
  { label: "Admission" },
  { label: "Parents" },
  { label: "Emergency" },
  { label: "Medical" },
  { label: "Review" },
];

export default function Application() {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    firstName: "", lastName: "", dateOfBirth: "", gender: "", nationality: "", placeOfBirth: "",
    admissionLevel: "", gradeApplying: "", academicYear: "2024/2025", startTerm: "", previousSchool: "", reasonForTransfer: "",
    parent1Title: "", parent1FirstName: "", parent1LastName: "", parent1Relationship: "", parent1Phone: "", parent1Email: "", parent1Occupation: "", parent1Address: "",
    parent2Title: "", parent2FirstName: "", parent2LastName: "", parent2Relationship: "", parent2Phone: "", parent2Email: "",
    emergencyName: "", emergencyRelationship: "", emergencyPhone: "", emergencyAddress: "",
    medicalConditions: "", allergies: "", medications: "", doctorName: "", doctorPhone: "", bloodGroup: "", specialNeeds: "",
    extracurricular: "", transportNeeded: false, lunchProgram: false, afterSchoolCare: false,
    agreeTerms: false, agreeAccuracy: false,
  });
  const [errors, setErrors] = useState({});
  const totalSteps = 6;

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: "" }));
  };

  const validateStep = (step) => {
    const e = {};
    if (step === 1) {
      if (!formData.firstName) e.firstName = "First name is required";
      if (!formData.lastName) e.lastName = "Last name is required";
      if (!formData.dateOfBirth) e.dateOfBirth = "Date of birth is required";
      if (!formData.gender) e.gender = "Gender is required";
      if (!formData.nationality) e.nationality = "Nationality is required";
    }
    if (step === 2) {
      if (!formData.admissionLevel) e.admissionLevel = "Admission level is required";
      if (!formData.gradeApplying) e.gradeApplying = "Grade is required";
      if (!formData.startTerm) e.startTerm = "Start term is required";
    }
    if (step === 3) {
      if (!formData.parent1FirstName) e.parent1FirstName = "First name is required";
      if (!formData.parent1LastName) e.parent1LastName = "Last name is required";
      if (!formData.parent1Phone) e.parent1Phone = "Phone number is required";
      if (!formData.parent1Email) e.parent1Email = "Email is required";
      if (!formData.parent1Relationship) e.parent1Relationship = "Relationship is required";
    }
    if (step === 4) {
      if (!formData.emergencyName) e.emergencyName = "Full name is required";
      if (!formData.emergencyPhone) e.emergencyPhone = "Phone number is required";
      if (!formData.emergencyRelationship) e.emergencyRelationship = "Relationship is required";
    }
    if (step === 6) {
      if (!formData.agreeTerms) e.agreeTerms = "You must agree to the terms";
      if (!formData.agreeAccuracy) e.agreeAccuracy = "You must confirm information accuracy";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, totalSteps));
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateStep(currentStep)) {
      alert("Application submitted successfully! We will contact you within 3–5 business days.");
    }
  };

  return (
    <div style={styles.page}>
      {/* Hero */}
      <section style={styles.hero}>
        <div style={styles.heroBadge}>
          <span style={styles.heroDot} />
          Online Application
        </div>
        <h1 style={styles.heroTitle}>Student Application Form</h1>
        <p style={styles.heroDesc}>
          Complete this form to apply for admission to Star Hacs International School.
          Fields marked with * are required.
        </p>
      </section>

      {/* Body */}
      <section style={styles.section}>
        <div style={styles.container}>

          {/* Stepper */}
          <div style={{ position: "relative", marginBottom: "40px" }}>
            <div style={styles.stepperLine} />
            <div style={styles.stepper}>
              {steps.map((s, i) => {
                const n = i + 1;
                const active = n === currentStep;
                const done = n < currentStep;
                return (
                  <div key={n} style={styles.stepItem}>
                    <div style={styles.stepBubble(active, done)}>
                      {done ? "✓" : n}
                    </div>
                    <span style={styles.stepLabel(active, done)}>{s.label}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Form Card */}
          <div style={styles.card}>
            <form onSubmit={handleSubmit}>

              {/* Step 1: Student Info */}
              {currentStep === 1 && (
                <>
                  <div style={styles.stepHead}>
                    <span style={styles.stepTag}>Step 1 of 6</span>
                    <h2 style={styles.stepTitle}>Student Information</h2>
                    <p style={styles.stepDesc}>Please provide the student's personal details.</p>
                  </div>
                  <div style={styles.grid}>
                    <Field label="First Name" required error={errors.firstName}>
                      <Input name="firstName" value={formData.firstName} onChange={handleInputChange} error={errors.firstName} placeholder="Enter first name" />
                    </Field>
                    <Field label="Last Name" required error={errors.lastName}>
                      <Input name="lastName" value={formData.lastName} onChange={handleInputChange} error={errors.lastName} placeholder="Enter last name" />
                    </Field>
                    <Field label="Date of Birth" required error={errors.dateOfBirth}>
                      <Input type="date" name="dateOfBirth" value={formData.dateOfBirth} onChange={handleInputChange} error={errors.dateOfBirth} />
                    </Field>
                    <Field label="Gender" required error={errors.gender}>
                      <Select name="gender" value={formData.gender} onChange={handleInputChange} error={errors.gender}>
                        <option value="">Select gender</option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                      </Select>
                    </Field>
                    <Field label="Nationality" required error={errors.nationality}>
                      <Input name="nationality" value={formData.nationality} onChange={handleInputChange} error={errors.nationality} placeholder="e.g. Ghanaian, British" />
                    </Field>
                    <Field label="Place of Birth">
                      <Input name="placeOfBirth" value={formData.placeOfBirth} onChange={handleInputChange} placeholder="City/Town, Country" />
                    </Field>
                  </div>
                </>
              )}

              {/* Step 2: Admission */}
              {currentStep === 2 && (
                <>
                  <div style={styles.stepHead}>
                    <span style={styles.stepTag}>Step 2 of 6</span>
                    <h2 style={styles.stepTitle}>Admission Details</h2>
                    <p style={styles.stepDesc}>Select the grade and academic year you are applying for.</p>
                  </div>
                  <div style={styles.grid}>
                    <Field label="Admission Level" required error={errors.admissionLevel}>
                      <Select name="admissionLevel" value={formData.admissionLevel} onChange={handleInputChange} error={errors.admissionLevel}>
                        <option value="">Select level</option>
                        <option value="preschool">Pre-School</option>
                        <option value="primary">Primary School</option>
                        <option value="jhs">Junior High School</option>
                      </Select>
                    </Field>
                    <Field label="Grade Applying For" required error={errors.gradeApplying}>
                      <Select name="gradeApplying" value={formData.gradeApplying} onChange={handleInputChange} error={errors.gradeApplying}>
                        <option value="">Select grade</option>
                        {formData.admissionLevel === "preschool" && (<>
                          <option value="toddlers">Toddlers</option>
                          <option value="reception1">Reception 1</option>
                          <option value="reception2">Reception 2</option>
                        </>)}
                        {formData.admissionLevel === "primary" && (<>
                          <option value="grade1">Grade 1</option>
                          <option value="grade2">Grade 2</option>
                          <option value="grade3">Grade 3</option>
                          <option value="grade4">Grade 4</option>
                          <option value="grade5">Grade 5</option>
                          <option value="grade6">Grade 6</option>
                        </>)}
                        {formData.admissionLevel === "jhs" && (
                          <option value="jhs1">JHS 1</option>
                        )}
                      </Select>
                    </Field>
                    <Field label="Academic Year">
                      <Input name="academicYear" value={formData.academicYear} onChange={handleInputChange} readOnly />
                    </Field>
                    <Field label="Starting Term" required error={errors.startTerm}>
                      <Select name="startTerm" value={formData.startTerm} onChange={handleInputChange} error={errors.startTerm}>
                        <option value="">Select term</option>
                        <option value="term1">Term 1 (September – December)</option>
                        <option value="term2">Term 2 (January – April)</option>
                        <option value="term3">Term 3 (May – July)</option>
                      </Select>
                    </Field>
                    <Field label="Previous School (if applicable)" full>
                      <Input name="previousSchool" value={formData.previousSchool} onChange={handleInputChange} placeholder="Enter previous school name" />
                    </Field>
                    <Field label="Reason for Transfer (if applicable)" full>
                      <Textarea name="reasonForTransfer" value={formData.reasonForTransfer} onChange={handleInputChange} placeholder="Please explain why you are transferring" rows={3} />
                    </Field>
                  </div>
                </>
              )}

              {/* Step 3: Parents */}
              {currentStep === 3 && (
                <>
                  <div style={styles.stepHead}>
                    <span style={styles.stepTag}>Step 3 of 6</span>
                    <h2 style={styles.stepTitle}>Parent / Guardian Information</h2>
                    <p style={styles.stepDesc}>Provide details for the primary contact and an optional secondary guardian.</p>
                  </div>

                  <div style={styles.subsection}>
                    <div style={styles.subsectionHead}>
                      <div style={styles.subsectionBar(true)} />
                      <span style={styles.subsectionTitle}>Primary Parent / Guardian</span>
                    </div>
                    <div style={styles.grid}>
                      <Field label="Title">
                        <Select name="parent1Title" value={formData.parent1Title} onChange={handleInputChange}>
                          <option value="">Select</option>
                          <option value="mr">Mr.</option>
                          <option value="mrs">Mrs.</option>
                          <option value="ms">Ms.</option>
                          <option value="dr">Dr.</option>
                        </Select>
                      </Field>
                      <Field label="Relationship" required error={errors.parent1Relationship}>
                        <Select name="parent1Relationship" value={formData.parent1Relationship} onChange={handleInputChange} error={errors.parent1Relationship}>
                          <option value="">Select relationship</option>
                          <option value="father">Father</option>
                          <option value="mother">Mother</option>
                          <option value="guardian">Guardian</option>
                          <option value="other">Other</option>
                        </Select>
                      </Field>
                      <Field label="First Name" required error={errors.parent1FirstName}>
                        <Input name="parent1FirstName" value={formData.parent1FirstName} onChange={handleInputChange} error={errors.parent1FirstName} placeholder="Enter first name" />
                      </Field>
                      <Field label="Last Name" required error={errors.parent1LastName}>
                        <Input name="parent1LastName" value={formData.parent1LastName} onChange={handleInputChange} error={errors.parent1LastName} placeholder="Enter last name" />
                      </Field>
                      <Field label="Phone Number" required error={errors.parent1Phone}>
                        <Input type="tel" name="parent1Phone" value={formData.parent1Phone} onChange={handleInputChange} error={errors.parent1Phone} placeholder="+233 XX XXX XXXX" />
                      </Field>
                      <Field label="Email Address" required error={errors.parent1Email}>
                        <Input type="email" name="parent1Email" value={formData.parent1Email} onChange={handleInputChange} error={errors.parent1Email} placeholder="email@example.com" />
                      </Field>
                      <Field label="Occupation" full>
                        <Input name="parent1Occupation" value={formData.parent1Occupation} onChange={handleInputChange} placeholder="Enter occupation" />
                      </Field>
                      <Field label="Residential Address" full>
                        <Textarea name="parent1Address" value={formData.parent1Address} onChange={handleInputChange} placeholder="Enter full address" rows={2} />
                      </Field>
                    </div>
                  </div>

                  <div style={styles.subsection}>
                    <div style={styles.subsectionHead}>
                      <div style={styles.subsectionBar(false)} />
                      <span style={styles.subsectionTitle}>Secondary Parent / Guardian (Optional)</span>
                    </div>
                    <div style={styles.grid}>
                      <Field label="Title">
                        <Select name="parent2Title" value={formData.parent2Title} onChange={handleInputChange}>
                          <option value="">Select</option>
                          <option value="mr">Mr.</option>
                          <option value="mrs">Mrs.</option>
                          <option value="ms">Ms.</option>
                          <option value="dr">Dr.</option>
                        </Select>
                      </Field>
                      <Field label="Relationship">
                        <Select name="parent2Relationship" value={formData.parent2Relationship} onChange={handleInputChange}>
                          <option value="">Select relationship</option>
                          <option value="father">Father</option>
                          <option value="mother">Mother</option>
                          <option value="guardian">Guardian</option>
                          <option value="other">Other</option>
                        </Select>
                      </Field>
                      <Field label="First Name">
                        <Input name="parent2FirstName" value={formData.parent2FirstName} onChange={handleInputChange} placeholder="Enter first name" />
                      </Field>
                      <Field label="Last Name">
                        <Input name="parent2LastName" value={formData.parent2LastName} onChange={handleInputChange} placeholder="Enter last name" />
                      </Field>
                      <Field label="Phone Number">
                        <Input type="tel" name="parent2Phone" value={formData.parent2Phone} onChange={handleInputChange} placeholder="+233 XX XXX XXXX" />
                      </Field>
                      <Field label="Email Address">
                        <Input type="email" name="parent2Email" value={formData.parent2Email} onChange={handleInputChange} placeholder="email@example.com" />
                      </Field>
                    </div>
                  </div>
                </>
              )}

              {/* Step 4: Emergency */}
              {currentStep === 4 && (
                <>
                  <div style={styles.stepHead}>
                    <span style={styles.stepTag}>Step 4 of 6</span>
                    <h2 style={styles.stepTitle}>Emergency Contact</h2>
                    <p style={styles.stepDesc}>Provide an alternative emergency contact (not the primary guardian).</p>
                  </div>
                  <div style={styles.grid}>
                    <Field label="Full Name" required error={errors.emergencyName}>
                      <Input name="emergencyName" value={formData.emergencyName} onChange={handleInputChange} error={errors.emergencyName} placeholder="Enter full name" />
                    </Field>
                    <Field label="Relationship" required error={errors.emergencyRelationship}>
                      <Input name="emergencyRelationship" value={formData.emergencyRelationship} onChange={handleInputChange} error={errors.emergencyRelationship} placeholder="e.g. Uncle, Aunt, Family Friend" />
                    </Field>
                    <Field label="Phone Number" required error={errors.emergencyPhone}>
                      <Input type="tel" name="emergencyPhone" value={formData.emergencyPhone} onChange={handleInputChange} error={errors.emergencyPhone} placeholder="+233 XX XXX XXXX" />
                    </Field>
                    <Field label="Address" full>
                      <Textarea name="emergencyAddress" value={formData.emergencyAddress} onChange={handleInputChange} placeholder="Enter full address" rows={2} />
                    </Field>
                  </div>
                </>
              )}

              {/* Step 5: Medical */}
              {currentStep === 5 && (
                <>
                  <div style={styles.stepHead}>
                    <span style={styles.stepTag}>Step 5 of 6</span>
                    <h2 style={styles.stepTitle}>Medical Information</h2>
                    <p style={styles.stepDesc}>Help us understand your child's health needs so we can provide the best care.</p>
                  </div>
                  <div style={styles.grid}>
                    <Field label="Known Medical Conditions" full>
                      <Textarea name="medicalConditions" value={formData.medicalConditions} onChange={handleInputChange} placeholder="e.g. Asthma, Diabetes, Epilepsy. Write 'None' if not applicable." rows={3} />
                    </Field>
                    <Field label="Allergies" full>
                      <Textarea name="allergies" value={formData.allergies} onChange={handleInputChange} placeholder="List any food, medication, or environmental allergies. Write 'None' if not applicable." rows={2} />
                    </Field>
                    <Field label="Current Medications" full>
                      <Textarea name="medications" value={formData.medications} onChange={handleInputChange} placeholder="List any regular medications and dosages." rows={2} />
                    </Field>
                    <Field label="Blood Group">
                      <Select name="bloodGroup" value={formData.bloodGroup} onChange={handleInputChange}>
                        <option value="">Select blood group</option>
                        {["A+","A-","B+","B-","AB+","AB-","O+","O-"].map(g => (
                          <option key={g} value={g}>{g}</option>
                        ))}
                      </Select>
                    </Field>
                    <Field label="Doctor's Name">
                      <Input name="doctorName" value={formData.doctorName} onChange={handleInputChange} placeholder="Enter doctor's name" />
                    </Field>
                    <Field label="Doctor's Phone">
                      <Input type="tel" name="doctorPhone" value={formData.doctorPhone} onChange={handleInputChange} placeholder="+233 XX XXX XXXX" />
                    </Field>
                    <Field label="Special Needs or Accommodations" full>
                      <Textarea name="specialNeeds" value={formData.specialNeeds} onChange={handleInputChange} placeholder="Describe any special educational or physical needs." rows={3} />
                    </Field>
                  </div>
                </>
              )}

              {/* Step 6: Review */}
              {currentStep === 6 && (
                <>
                  <div style={styles.stepHead}>
                    <span style={styles.stepTag}>Step 6 of 6</span>
                    <h2 style={styles.stepTitle}>Review &amp; Submit</h2>
                    <p style={styles.stepDesc}>Almost there — select any additional services and confirm your declaration.</p>
                  </div>

                  <div style={{ marginBottom: "24px" }}>
                    <p style={{ fontSize: "13px", fontWeight: "700", color: COLORS.navy, textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: "14px" }}>Additional Services</p>
                    <ServiceCheck name="transportNeeded" checked={formData.transportNeeded} onChange={handleInputChange} label="School Bus Transportation" icon="🚌" />
                    <ServiceCheck name="lunchProgram" checked={formData.lunchProgram} onChange={handleInputChange} label="School Lunch Program" icon="🍽️" />
                    <ServiceCheck name="afterSchoolCare" checked={formData.afterSchoolCare} onChange={handleInputChange} label="After-School Care (until 4:30 PM)" icon="🕟" />
                  </div>

                  <div style={{ marginBottom: "28px" }}>
                    <p style={{ fontSize: "13px", fontWeight: "700", color: COLORS.navy, textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: "14px" }}>Extracurricular Interests (Optional)</p>
                    <Textarea name="extracurricular" value={formData.extracurricular} onChange={handleInputChange} placeholder="List any sports, arts, or activities your child is interested in." rows={3} />
                  </div>

                  <div style={styles.declaration}>
                    <p style={styles.declarationTitle}>Declaration &amp; Agreement</p>
                    <AgreeRow name="agreeTerms" checked={formData.agreeTerms} onChange={handleInputChange} error={errors.agreeTerms}>
                      I agree to the school's <a href="/terms" target="_blank" style={{ color: "#f4a0a1" }}>Terms and Conditions</a> and understand the admission policies and fee structure.
                    </AgreeRow>
                    {errors.agreeTerms && <p style={{ ...styles.errorMsg, color: "#F87171", marginBottom: "8px", paddingLeft: "4px" }}>{errors.agreeTerms}</p>}
                    <AgreeRow name="agreeAccuracy" checked={formData.agreeAccuracy} onChange={handleInputChange} error={errors.agreeAccuracy}>
                      I certify that all information provided in this application is true and accurate to the best of my knowledge.
                    </AgreeRow>
                    {errors.agreeAccuracy && <p style={{ ...styles.errorMsg, color: "#F87171", marginBottom: "8px", paddingLeft: "4px" }}>{errors.agreeAccuracy}</p>}
                    <div style={styles.note}>
                      <strong>What happens next?</strong> After submission, you will receive a confirmation email with further instructions. An admissions officer will contact you within 3–5 business days.
                    </div>
                  </div>
                </>
              )}

              {/* Nav Buttons */}
              <div style={styles.nav}>
                {currentStep > 1 && (
                  <button type="button" onClick={prevStep} style={styles.btnOutline}>
                    ← Previous
                  </button>
                )}
                {currentStep < totalSteps ? (
                  <button type="button" onClick={nextStep} style={styles.btnPrimary}>
                    Continue →
                  </button>
                ) : (
                  <button type="submit" style={styles.btnSubmit}>
                    Submit Application ✓
                  </button>
                )}
              </div>
            </form>
          </div>

          {/* Help Bar */}
          <div style={styles.helpBar}>
            <p style={styles.helpText}>
              <strong style={{ color: COLORS.navy }}>Need assistance?</strong>{" "}
              Contact our admissions office for help with this form.
            </p>
            <div style={styles.helpContacts}>
              <span style={styles.helpContact}>📞 +233 59 703 4048</span>
              <span style={styles.helpContact}>✉️ starhacsinternational@gmail.com</span>
            </div>
          </div>
        </div>
      </section>

      {/* Responsive CSS */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700&family=DM+Sans:wght@400;500;700&display=swap');
        * { box-sizing: border-box; }
        @media (max-width: 640px) {
          .app-grid-override { grid-template-columns: 1fr !important; }
        }
        input[type="date"]::-webkit-calendar-picker-indicator { opacity: 0.5; cursor: pointer; }
        input[readonly] { background: #F5F7FB !important; color: #6B7280 !important; cursor: default; }
        button:hover { opacity: 0.88; transform: translateY(-1px); }
        button:active { transform: scale(0.98); }
        select option { color: #111827; background: white; }
        @media (max-width: 680px) {
          .app-step-label { display: none; }
        }
      `}</style>

      {/* Responsive grid override via JS */}
      <style>{`
        @media (max-width: 600px) {
          [data-grid] { grid-template-columns: 1fr !important; }
          [data-card] { padding: 24px 18px !important; }
          [data-nav] { flex-direction: column-reverse !important; }
          [data-nav] button { width: 100% !important; justify-content: center !important; margin-left: 0 !important; }
          [data-hero] { padding: 52px 20px 48px !important; }
          [data-help] { flex-direction: column !important; align-items: flex-start !important; }
        }
      `}</style>
    </div>
  );
}